import stringSimilarity from 'string-similarity';
import { format } from 'date-fns';
import { updateRVUand$Value } from '../../../../utils/auditsheet.utils';
import { generateOptions } from '../../../../utils/responseColumn';


const FILE_HEADER_MAPPING = {
    "encounter_no": ['Encounter #'],
    "rendering": ['Rendering'],
    "enc_dt": ['Enc Dt'],
    "notes": ['Notes'],
    "billed_icd": ['Billed ICD'],
    "srvcs_no": ['Billed CPT'],
    "provider_education": ['Provider Education'],
    "number_complexity": ['Number & Complexity'],
    "amount_complexity": ['Amount & Complexity'],
    "risk_complications": ['Mortality of Patient Mgmt'],
}

const REQUIRED_FIELDS = {
    "missing_info": null,
    "encounter_no": null,
    "rendering": null,
    "enc_dt": null,
    "billed_icd": null,
    "srvcs_no": null,
    "billed_modifier": null,
    "provider_rvu": null,
    "provider_dollar_value": null,
    "response": null,
    "agree": null,
    "disagree": null,
    "icd_agree": null,
    "icd_disagree": null,
    "audited_icd": null,
    "audited_cpt": null,
    "audited_modifier": null,
    "audited_rvu": null,
    "audited_dollar_value": null,
    "notes": null,
    "provider_education": null,
    "number_complexity": null,
    "amount_complexity": null,
    "risk_complications": null,
    "file_name": null,
    "chart_id": null,
    "row_id": null,
    "id": null
}

const MAX_CHAR_LIMIT = 200;

const validateFieldLength = (key, value, rowIndex, overflowWarnings) => {
    if (typeof value === 'string' && value.length > MAX_CHAR_LIMIT) {
        overflowWarnings.push({
            row: rowIndex + 2, // map to excel row
            field: key,
            preview: value.slice(0, 20) + '...'
        });
        return "";
    }
    return value;
};

const parseNumeric = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const cleaned = String(value).replace(/[$,\s]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : parseFloat(num.toFixed(2));
};

export const processSheedData = (
    excelData,
    headerMapping,
    checkboxState,
    providerOptions,
    currentUpload,
    industryCodes,
    additionalIcdMappings = [],
    additionalCptMappings = [],
    additionalModifierMappings = [],
    auditedConfig = {},
    responseAuditIssues = []
) => {
    let result = {
        "index": 1,
        "sheet_name": "Sheet1",
        "data": []
    };
    const overflowWarnings = [];
    validateExcelHeaders(excelData);
    result.data = excelData.map((row, rowIndex) => {
        let mappedRow = { ...REQUIRED_FIELDS };
        Object.keys(FILE_HEADER_MAPPING).forEach(key => {

            mappedRow['chart_id'] = currentUpload.id;
            mappedRow['file_name'] = currentUpload.upload_id;

            if (key === "billed_icd") {
                mappedRow = {...mappedRow, ...mapICD(row, headerMapping, additionalIcdMappings, checkboxState.ICDCommaSeparated)};
            } else if (key === "srvcs_no") {
                mappedRow = {...mappedRow, ...mapCPT(row, headerMapping, additionalCptMappings, additionalModifierMappings, checkboxState.CPTCommaSeparated, checkboxState.cptModifierAttached)};
            } else {
                // For other keys, map as normal
                const data = FILE_HEADER_MAPPING[key]
                    .map(h => row[headerMapping[h]])
                    .find(value => value !== undefined);
                if (data !== undefined) {
                    if(key === "rendering"){
                        mappedRow[key] = getProperPhysicianName(data, providerOptions);
                    } else if(key === "enc_dt"){
                        mappedRow[key] = formatDate(data);
                    } else if(key === "notes"){
                        let value = data;
                        value = validateFieldLength(key, value, rowIndex, overflowWarnings);
                        mappedRow[key] = value;
                    } else if(key === "provider_education"){
                        let value = data;
                        value = validateFieldLength(key, value, rowIndex, overflowWarnings);
                        mappedRow[key] = value;
                    } else if(key === "number_complexity" || key === "amount_complexity" || key === "risk_complications"){
                        let value = data;
                        value = validateFieldLength(key, value, rowIndex, overflowWarnings);
                        mappedRow[key] = value;
                    } else if(key === "encounter_no"){
                        let value = data;
                        value = validateFieldLength(key, value, rowIndex, overflowWarnings);
                        mappedRow[key] = value;
                    }
                }
            }
        });
        // Audited field mapping — applied after billed so agree-column/agree-all modes can reference mapped billed data
        const { icdMode, cptMode } = auditedConfig;
        if (icdMode === 'importColumns') {
            mappedRow = { ...mappedRow, ...mapAuditedICD(row, headerMapping, auditedConfig.additionalIcdMappings, auditedConfig.icdCommaSeparated) };
            mappedRow = { ...mappedRow, ...autoIcdAgree(mappedRow) };
        } else if (icdMode === 'agreeColumn') {
            mappedRow = { ...mappedRow, ...mapIcdAgreeColumn(row, headerMapping, mappedRow) };
        } else if (icdMode === 'agreeAll') {
            mappedRow = { ...mappedRow, ...mapIcdAgreeAll(mappedRow) };
        }
        if (cptMode === 'importColumns') {
            mappedRow = { ...mappedRow, ...mapAuditedCPT(row, headerMapping, auditedConfig.additionalCptMappings, auditedConfig.additionalModifierMappings, auditedConfig.cptCommaSeparated, auditedConfig.modifierAttached) };
            mappedRow = { ...mappedRow, ...autoCptAgree(mappedRow) };
        } else if (cptMode === 'agreeColumn') {
            mappedRow = { ...mappedRow, ...mapCptAgreeColumn(row, headerMapping, mappedRow) };
        } else if (cptMode === 'agreeAll') {
            mappedRow = { ...mappedRow, ...mapCptAgreeAll(mappedRow) };
        }

        mappedRow = { ...mappedRow, ...mapResponse(row, headerMapping, mappedRow, responseAuditIssues) };

        return mappedRow;
    });
    result.data = calculate$Value(result, industryCodes);

    // Apply Excel RVU/$ overrides after auto-calc so they take precedence
    const rvuMapped = headerMapping['Billed RVU'] || headerMapping['Billed $ Value'] ||
                      headerMapping['Audited RVU'] || headerMapping['Audited $ Value'];
    if (rvuMapped) {
        result.data = result.data.map((row, idx) => {
            const excelRow = excelData[idx];
            if (!excelRow) return row;
            const updated = { ...row };
            if (headerMapping['Billed RVU']) {
                const v = parseNumeric(excelRow[headerMapping['Billed RVU']]);
                if (v !== null) updated['provider_rvu'] = v;
            }
            if (headerMapping['Billed $ Value']) {
                const v = parseNumeric(excelRow[headerMapping['Billed $ Value']]);
                if (v !== null) updated['provider_dollar_value'] = v;
            }
            if (headerMapping['Audited RVU']) {
                const v = parseNumeric(excelRow[headerMapping['Audited RVU']]);
                if (v !== null) updated['audited_rvu'] = v;
            }
            if (headerMapping['Audited $ Value']) {
                const v = parseNumeric(excelRow[headerMapping['Audited $ Value']]);
                if (v !== null) updated['audited_dollar_value'] = v;
            }
            return updated;
        });
    }

    result.warnings = overflowWarnings;
    return result;
}

const calculate$Value = (result, industryCodes) => {
    let processdata = result.data;
    processdata.forEach((value, index) => {
        processdata = updateRVUand$Value(processdata, index, industryCodes);
    });
    return processdata;
}

const AGREE_AUDITS_VALUE = '-1';
const AGREE_AUDITS_LABEL = 'Agree Audits';
const RESPONSE_WEIGHT_CAP = 10;

const normalizeForMatch = (s) => String(s || '').toLowerCase().replace(/\s+/g, '').trim();

// Matches typed Response text against the same candidate list the live grid would show
// for this row — static options plus the dynamic ones generated from its own billed/audited
// ICD/CPT/Modifier codes (reuses generateOptions so the two never drift out of sync).
// Fuzzy matching only applies to static (human-authored) names; dynamic names embed an
// exact code, so a near-miss there is more likely a wrong code than a typo and must not be
// silently substituted. Selection accumulates in cell order; a token whose weight would push
// the running total over the 10-point cap is skipped (not a hard stop) so a later, smaller
// match can still be taken if it fits within what's left of the cap. Never throws and never
// records a warning — an unmatched or over-cap token is simply dropped so one bad cell can't
// interrupt a large import.
const mapResponse = (row, headerMapping, mappedRow, responseAuditIssues) => {
    try {
        const raw = row[headerMapping['Response']];
        if (!raw) return {};

        const tokens = String(raw).split(',').map(t => t.trim()).filter(Boolean);
        if (!tokens.length) return {};

        if (tokens.some(t => normalizeForMatch(t) === normalizeForMatch(AGREE_AUDITS_LABEL))) {
            return { response: AGREE_AUDITS_VALUE };
        }

        const candidates = generateOptions(mappedRow, responseAuditIssues);
        if (!candidates.length) return {};
        const staticCandidates = candidates.filter(c => !String(c.id).includes(':'));

        const selectedIds = [];
        let runningWeight = 0;

        for (const token of tokens) {
            const normalizedToken = normalizeForMatch(token);
            let match = candidates.find(c => normalizeForMatch(c.name) === normalizedToken && !selectedIds.includes(c.id));

            if (!match && staticCandidates.length) {
                const { bestMatch, bestMatchIndex } = stringSimilarity.findBestMatch(
                    normalizedToken,
                    staticCandidates.map(c => normalizeForMatch(c.name))
                );
                if (bestMatch.rating >= 0.6) match = staticCandidates[bestMatchIndex];
            }

            if (!match || selectedIds.includes(match.id)) continue;

            const weight = match.updated_weight || 0;
            if (runningWeight + weight > RESPONSE_WEIGHT_CAP) continue;

            selectedIds.push(match.id);
            runningWeight += weight;
        }

        return selectedIds.length ? { response: selectedIds.join(',') } : {};
    } catch {
        return {};
    }
};

const mapICD = (row, headerMapping, additionalIcdMappings = [], commaSeperated = false) => {
    let count = 1;
    let data = {};

    const addICDData = (icd) => {
        if (icd === null || icd === undefined || icd === '') return;
        const key = count === 1 ? 'billed_icd' : `billed_icd_${count}`;
        data[key] = icd;
        count++;
    };

    if (commaSeperated) {
        const rawValue = row[headerMapping["Billed ICD"]];
        if (rawValue) {
            String(rawValue).split(",").map(item => item.replace(/\s+/g, "")).forEach(addICDData);
        }
    } else {
        // Primary column — exact match only
        addICDData(row[headerMapping["Billed ICD"]]);
        // Additional columns — explicitly selected by the user
        additionalIcdMappings.forEach(colName => {
            if (colName) addICDData(row[colName]);
        });
    }

    return data;
};

// Extracts a "*N" units suffix from anywhere in a token — units are an independent
// attribute of a claim line (not positionally tied to modifier placement) — and returns
// the remaining "core" string with that marker removed.
const extractUnits = (token) => {
    const match = token.match(/\*(\d+)/);
    const units = match ? match[1] : '';
    const core = token.replace(/\*\d+/, '');
    return { core, units };
};

// "Attached" mode: modifier(s) are hyphen-joined onto the CPT itself, e.g. "99213-25-TC*3".
// Units are pulled out first (order-agnostic), then whatever remains is hyphen-split into
// code + modifier(s), comma-joined if there's more than one. Never throws — an unparseable
// token returns null and is skipped, not fatal.
export const parseAttachedToken = (token) => {
    try {
        const { core, units } = extractUnits(token);
        const [rawCpt, ...modifierParts] = core.split('-');
        const cpt = (rawCpt || '').trim();
        if (!cpt) return null;
        const modifier = modifierParts.map(m => m.trim()).filter(Boolean).join(',');
        return { cpt: units ? `${cpt}*${units}` : cpt, modifier };
    } catch {
        return null;
    }
};

// "Separate" mode: the modifier lives elsewhere, so the CPT token is never hyphen-split —
// only units are pulled out. A stray hyphen already in the source data is left as a literal
// part of the CPT string rather than silently reinterpreted as a modifier.
export const parseSeparateToken = (token) => {
    try {
        const { core, units } = extractUnits(token);
        const cpt = core.trim();
        if (!cpt) return null;
        return { cpt: units ? `${cpt}*${units}` : cpt };
    } catch {
        return null;
    }
};

// Case A — comma-separated CPT, modifier attached via hyphen on each token. A token that
// fails to parse is skipped so it never blocks the rest of the row.
export const parseCommaSeparatedAttached = (rawValue) => {
    const pairs = [];
    if (!rawValue) return pairs;
    String(rawValue).split(',').forEach(token => {
        const trimmed = token.trim();
        if (!trimmed) return;
        const pair = parseAttachedToken(trimmed);
        if (pair) pairs.push(pair);
    });
    return pairs;
};

// Case (attached + mapped columns) — one CPT per mapped column, each of which may itself
// carry a hyphen-attached modifier, e.g. column value "99213-25". No comma-splitting.
export const parseMappedAttached = (cptValues) => {
    const pairs = [];
    cptValues.forEach((cptValue) => {
        if (cptValue === null || cptValue === undefined || cptValue === '') return;
        const pair = parseAttachedToken(String(cptValue).trim());
        if (pair) pairs.push(pair);
    });
    return pairs;
};

// Case B — CPT and modifier each comma-separated, matched by position (e.g. CPT
// "99213, 99214, 99215" / Modifier "25, , 59"). A blank/unparseable CPT token drops that
// position (CPT + modifier together) and compacts; a blank modifier token is kept as blank.
export const parseCommaSeparatedSeparate = (rawCptValue, rawModifierValue) => {
    const pairs = [];
    if (!rawCptValue) return pairs;
    const cptTokens = String(rawCptValue).split(',');
    const modTokens = rawModifierValue ? String(rawModifierValue).split(',') : [];
    cptTokens.forEach((cptToken, i) => {
        const pair = parseSeparateToken(cptToken.trim());
        if (!pair) return;
        const modifier = modTokens[i] ? modTokens[i].trim() : '';
        pairs.push({ cpt: pair.cpt, modifier });
    });
    return pairs;
};

// Case C — CPT columns paired 1:1 with modifier columns (primary + additional, same
// index). A blank/unparseable CPT drops that pair and compacts; a blank modifier is kept.
export const parseMappedSeparate = (cptValues, modifierValues) => {
    const pairs = [];
    cptValues.forEach((cptValue, i) => {
        if (cptValue === null || cptValue === undefined || cptValue === '') return;
        const pair = parseSeparateToken(String(cptValue).trim());
        if (!pair) return;
        const modifierValue = modifierValues[i];
        const modifier = (modifierValue !== null && modifierValue !== undefined && modifierValue !== '')
            ? String(modifierValue).trim() : '';
        pairs.push({ cpt: pair.cpt, modifier });
    });
    return pairs;
};

const mapCPT = (
    row,
    headerMapping,
    additionalCptMappings = [],
    additionalModifierMappings = [],
    commaSeperated = false,
    modifierAttached = false,
) => {
    let data = {};

    let pairs;
    if (commaSeperated) {
        const rawCpt = row[headerMapping["Billed CPT"]];
        pairs = modifierAttached
            ? parseCommaSeparatedAttached(rawCpt)
            : parseCommaSeparatedSeparate(rawCpt, row[headerMapping["Modifier"]]);
    } else {
        const cptValues = [row[headerMapping["Billed CPT"]], ...additionalCptMappings.map(col => col ? row[col] : undefined)];
        pairs = modifierAttached
            ? parseMappedAttached(cptValues)
            : parseMappedSeparate(cptValues, [row[headerMapping["Modifier"]], ...additionalModifierMappings.map(col => col ? row[col] : undefined)]);
    }

    pairs.forEach((pair, i) => {
        const n = i + 1;
        data[n === 1 ? 'srvcs_no' : `srvcs_no_${n}`] = pair.cpt;
        if (pair.modifier) data[n === 1 ? 'billed_modifier' : `billed_modifier_${n}`] = pair.modifier;
    });

    return data;
}

// ── Audited field mappers ─────────────────────────────────────────────────────

const mapAuditedICD = (row, headerMapping, additionalMappings = [], commaSeparated = false) => {
    let data = {};
    let count = 1;
    const add = (icd) => {
        if (icd === null || icd === undefined || icd === '') return;
        data[count === 1 ? 'audited_icd' : `audited_icd_${count}`] = icd;
        count++;
    };
    if (commaSeparated) {
        const raw = row[headerMapping['Audited ICD']];
        if (raw) String(raw).split(',').map(s => s.replace(/\s+/g, '')).forEach(add);
    } else {
        add(row[headerMapping['Audited ICD']]);
        additionalMappings.forEach(col => { if (col) add(row[col]); });
    }
    return data;
};

const mapAuditedCPT = (row, headerMapping, additionalCptMappings = [], additionalModifierMappings = [], commaSeparated = false, modifierAttached = false) => {
    let data = {};

    let pairs;
    if (commaSeparated) {
        const rawCpt = row[headerMapping['Audited CPT']];
        pairs = modifierAttached
            ? parseCommaSeparatedAttached(rawCpt)
            : parseCommaSeparatedSeparate(rawCpt, row[headerMapping['Audited Modifier']]);
    } else {
        const cptValues = [row[headerMapping['Audited CPT']], ...additionalCptMappings.map(col => col ? row[col] : undefined)];
        pairs = modifierAttached
            ? parseMappedAttached(cptValues)
            : parseMappedSeparate(cptValues, [row[headerMapping['Audited Modifier']], ...additionalModifierMappings.map(col => col ? row[col] : undefined)]);
    }

    pairs.forEach((pair, i) => {
        const n = i + 1;
        data[n === 1 ? 'audited_cpt' : `audited_cpt_${n}`] = pair.cpt;
        if (pair.modifier) data[n === 1 ? 'audited_modifier' : `audited_modifier_${n}`] = pair.modifier;
    });

    return data;
};

// Auto-computes agree/disagree by comparing billed vs audited sequences in mappedRow

const seqOf = (obj, singularKey) => {
    const vals = [];
    if (obj[singularKey] != null && obj[singularKey] !== '') vals.push(String(obj[singularKey]).trim().toUpperCase());
    let i = 2;
    while (obj[`${singularKey}_${i}`] != null && obj[`${singularKey}_${i}`] !== '') {
        vals.push(String(obj[`${singularKey}_${i}`]).trim().toUpperCase());
        i++;
    }
    return vals;
};

const autoIcdAgree = (mappedRow) => {
    const billed = seqOf(mappedRow, 'billed_icd');
    const audited = seqOf(mappedRow, 'audited_icd');
    if (billed.length === 0 && audited.length === 0) return {};
    const agree = billed.length === audited.length && billed.every((v, i) => v === audited[i]);
    return { icd_agree: agree, icd_disagree: !agree };
};

const autoCptAgree = (mappedRow) => {
    const billedCpt = seqOf(mappedRow, 'srvcs_no');
    const auditedCpt = seqOf(mappedRow, 'audited_cpt');
    if (billedCpt.length === 0 && auditedCpt.length === 0) return {};
    const cptMatch = billedCpt.length === auditedCpt.length && billedCpt.every((v, i) => v === auditedCpt[i]);
    // Only factor in modifier if audited modifier was actually mapped
    const auditedMod = seqOf(mappedRow, 'audited_modifier');
    let modMatch = true;
    if (auditedMod.length > 0) {
        const billedMod = seqOf(mappedRow, 'billed_modifier');
        modMatch = billedMod.length === auditedMod.length && billedMod.every((v, i) => v === auditedMod[i]);
    }
    const agree = cptMatch && modMatch;
    return { agree, disagree: !agree };
};

// Agree/disagree column modes — derive audited values from already-mapped billed data

const mapIcdAgreeColumn = (row, headerMapping, mappedRow) => {
    const agreeCol = headerMapping['ICD Agree'];
    if (!agreeCol) return {};
    const isAgree = String(row[agreeCol] ?? '').trim().toLowerCase() === 'yes';
    const data = { icd_agree: isAgree, icd_disagree: !isAgree };
    if (isAgree) {
        Object.entries(mappedRow).forEach(([key, val]) => {
            const m = key.match(/^billed_icd(?:_(\d+))?$/);
            if (m) data[m[1] ? `audited_icd_${m[1]}` : 'audited_icd'] = val;
        });
    }
    return data;
};

const mapCptAgreeColumn = (row, headerMapping, mappedRow) => {
    const agreeCol = headerMapping['CPT Agree'];
    if (!agreeCol) return {};
    const isAgree = String(row[agreeCol] ?? '').trim().toLowerCase() === 'yes';
    const data = { agree: isAgree, disagree: !isAgree };
    if (isAgree) {
        Object.entries(mappedRow).forEach(([key, val]) => {
            const cptM = key.match(/^srvcs_no(?:_(\d+))?$/);
            if (cptM) data[cptM[1] ? `audited_cpt_${cptM[1]}` : 'audited_cpt'] = val;
            const modM = key.match(/^billed_modifier(?:_(\d+))?$/);
            if (modM) data[modM[1] ? `audited_modifier_${modM[1]}` : 'audited_modifier'] = val;
        });
    }
    return data;
};

// Agree-all mode — unconditionally copies billed values to audited, same field-matching
// as the agree-column "yes" branch above, without needing a source column at all.

const mapIcdAgreeAll = (mappedRow) => {
    const data = { icd_agree: true, icd_disagree: false };
    Object.entries(mappedRow).forEach(([key, val]) => {
        const m = key.match(/^billed_icd(?:_(\d+))?$/);
        if (m) data[m[1] ? `audited_icd_${m[1]}` : 'audited_icd'] = val;
    });
    return data;
};

const mapCptAgreeAll = (mappedRow) => {
    const data = { agree: true, disagree: false };
    Object.entries(mappedRow).forEach(([key, val]) => {
        const cptM = key.match(/^srvcs_no(?:_(\d+))?$/);
        if (cptM) data[cptM[1] ? `audited_cpt_${cptM[1]}` : 'audited_cpt'] = val;
        const modM = key.match(/^billed_modifier(?:_(\d+))?$/);
        if (modM) data[modM[1] ? `audited_modifier_${modM[1]}` : 'audited_modifier'] = val;
    });
    return data;
};

const getProperPhysicianName = (sheetName, providerOptions) => {
    if (sheetName && providerOptions.length > 0) {
        const match = stringSimilarity.findBestMatch(sheetName.toLowerCase(),  providerOptions.map(obj => obj.value.toLowerCase()));
       
        if (match.bestMatch.rating > 0.6) {
            // Get the original object that corresponds to the matched string
            const bestMatchObject = providerOptions.find(obj => obj.value.toLowerCase() === match.bestMatch.target);
            return bestMatchObject.value;
        } else {
            return null;
        }
    }
    return null;
}

const formatDate = (dateStr) => {
    try{
        if(dateStr){
            const parsedDate = new Date(dateStr);
            const formattedDate = format(parsedDate, 'MM-dd-yyyy');
            const enc_dt = /^(?:\d{2}-\d{2}-\d{2}|\d{2}-\d{2}-\d{4})$/
            if (enc_dt.test(formattedDate)) {
                return formattedDate;
            }else if(isNaN(parsedDate.getTime())){
                return null;
            }
        }
        return null;
    }catch{
        return null;
    }
};




function validateExcelHeaders(excelData) {
    if (!Array.isArray(excelData) || excelData.length === 0) {
        throw new Error("Invalid Excel data: no rows found.");
    }

    const firstRow = excelData[0];
    if (typeof firstRow !== 'object') {
        throw new Error("Invalid Excel data: rows should be objects.");
    }
}
